import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { CacheKeys } from '../../lib/cache'
import { DocumentType, documentTypeLabels, useOrgDocuments } from '../../models/document'
import { sendCorgiDocuments } from '../../models/insuranceIndication'

// Mirrors CORGI_DOCUMENT_TYPE_MAP/CORGI_V2_DOCUMENT_TYPE_MAP in backend/insure/services/carriers/.
const SENDABLE_DOCUMENT_TYPES: DocumentType[] = [DocumentType.LOSS_RUN, DocumentType.MVR, DocumentType.IFTA]

interface SendCorgiDocumentsDialogProps {
  orgId: string
  indicationId: string
  /** Needed to invalidate the application query so the card refetches after a send. */
  applicationId?: string
  children: React.ReactNode
}

interface DocumentSendResult {
  documentId: string
  success: boolean
  queued?: boolean
  error: string | null
}

export function SendCorgiDocumentsDialog({
  orgId,
  indicationId,
  applicationId,
  children,
}: SendCorgiDocumentsDialogProps) {
  const queryClient = useQueryClient()
  const { documents, isLoading } = useOrgDocuments(orgId, SENDABLE_DOCUMENT_TYPES)
  const [open, setOpen] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [results, setResults] = useState<DocumentSendResult[] | null>(null)
  const [isSending, setIsSending] = useState(false)

  const sendableDocuments = documents.filter((document) =>
    SENDABLE_DOCUMENT_TYPES.includes(document.documentType)
  )

  function reset() {
    setSelectedIds(new Set())
    setResults(null)
  }

  function handleOpenChange(next: boolean) {
    if (!next) reset()
    setOpen(next)
  }

  function toggleSelected(documentId: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(documentId)) {
        next.delete(documentId)
      } else {
        next.add(documentId)
      }
      return next
    })
  }

  async function handleSend() {
    setIsSending(true)
    try {
      const sendResults = await sendCorgiDocuments(orgId, indicationId, Array.from(selectedIds))
      setResults(sendResults)
      if (sendResults.some((result) => result.sentCount > 0)) {
        // Refetch on any partial send too, not just full success — covers both app-fetching hooks (APPLICATION vs staff-page ORGANISATIONS key).
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: [CacheKeys.APPLICATION, applicationId] }),
          queryClient.invalidateQueries({ queryKey: [CacheKeys.ORGANISATIONS, orgId, 'application'] }),
        ])
      }
      if (sendResults.every((result) => result.success)) {
        toast.success(sendResults.some((result) => result.queued) ? 'Queued for Corgi' : 'Sent to Corgi')
      } else {
        toast.error('Some documents failed to send — see details below')
      }
    } catch {
      toast.error('Failed to send documents to Corgi')
    } finally {
      setIsSending(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>Send documents to Corgi</DialogTitle>
          <DialogDescription>Select any on-file documents to send to this quote.</DialogDescription>
        </DialogHeader>

        <div className="flex max-h-80 flex-col gap-2 overflow-y-auto">
          {isLoading && <div className="text-muted-foreground text-sm">Loading documents…</div>}
          {!isLoading && sendableDocuments.length === 0 && (
            <div className="text-muted-foreground text-sm">
              No loss run, MVR, or IFTA documents on file for this org.
            </div>
          )}
          {sendableDocuments.map((document) => {
            const result = results?.find((r) => r.documentId === document.id)
            return (
              <label key={document.id} className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={selectedIds.has(document.id)}
                  onClick={() => toggleSelected(document.id)}
                  disabled={isSending || !!results}
                />
                <span className="flex-1">
                  {document.attachments.map((attachment) => attachment.fileName).join(', ')}
                  <span className="text-muted-foreground ml-2">
                    ({documentTypeLabels[document.documentType]})
                  </span>
                </span>
                {result && (
                  <span className={result.success ? 'text-accent-green' : 'text-error'}>
                    {result.success ? (result.queued ? 'Queued' : 'Sent') : (result.error ?? 'Failed')}
                  </span>
                )}
              </label>
            )
          })}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isSending}>
            {results ? 'Close' : 'Cancel'}
          </Button>
          {!results && (
            <Button
              onClick={handleSend}
              disabled={selectedIds.size === 0 || isSending}
              loading={isSending ? 'Sending…' : undefined}
            >
              Send to Corgi
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
